import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  consentApi,
  organizationConsentModeApi,
  RequestError,
  type ConsentMasterModesResponse,
  type TenantConsentMode,
  type TenantConsentModeResponse,
  type TenantConsentModeSwitchResponse,
} from '../api/client';

/**
 * ADM-S07 組織データ利用モード管理（2026-04-17 仕様）。
 * テナント全体のモードを admin が切り替える。個人特定 → 匿名への切替は全ユーザーの
 * 個人特定同意を revoke + 全員分の匿名化申請を自動投入するため、確認ダイアログで強く警告する
 */
export default function AdminOrganizationMode() {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const [mode, setMode] = useState<TenantConsentModeResponse | null>(null);
  const [masterModes, setMasterModes] = useState<ConsentMasterModesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<TenantConsentMode | null>(null);
  const [confirmAck, setConfirmAck] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<TenantConsentModeSwitchResponse | null>(null);

  const load = useCallback(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    setError(null);
    setLoading(true);
    Promise.all([
      organizationConsentModeApi.adminGet(token),
      consentApi.getMasterModes(token).catch((err: unknown) => {
        // BE が master-modes 未実装でも画面は出せるようにする
        if (err instanceof RequestError && err.status === 404) return null;
        throw err;
      }),
    ])
      .then(([modeRes, masterRes]) => {
        setMode(modeRes);
        // `{}` が返る古い BE を考慮して配列ごとにフォールバック
        const normalized =
          masterRes &&
          (Array.isArray(masterRes.anonymous) ||
            Array.isArray(masterRes.personal_identified) ||
            Array.isArray(masterRes.common))
            ? {
                anonymous: Array.isArray(masterRes.anonymous) ? masterRes.anonymous : [],
                personal_identified: Array.isArray(masterRes.personal_identified)
                  ? masterRes.personal_identified
                  : [],
                common: Array.isArray(masterRes.common) ? masterRes.common : [],
              }
            : null;
        setMasterModes(normalized);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('dashboard.errorLoad'));
      })
      .finally(() => setLoading(false));
  }, [getToken, t]);

  useEffect(() => {
    load();
  }, [load]);

  const openConfirm = (target: TenantConsentMode) => {
    if (!mode || mode.consent_mode === target) return;
    setConfirmTarget(target);
    setConfirmAck(false);
    setSwitchError(null);
  };

  const closeConfirm = () => {
    if (switching) return;
    setConfirmTarget(null);
    setConfirmAck(false);
  };

  const handleSwitch = async () => {
    if (!confirmTarget) return;
    const token = getToken();
    if (!token) return;
    setSwitching(true);
    setSwitchError(null);
    try {
      const res = await organizationConsentModeApi.adminSwitch({ mode: confirmTarget }, token);
      setLastResult(res);
      setConfirmTarget(null);
      setConfirmAck(false);
      load();
    } catch (err) {
      setSwitchError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setSwitching(false);
    }
  };

  if (loading) {
    return (
      <div className="page admin-org-mode-page">
        <div className="dashboard-loading">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page admin-org-mode-page">
        <h1>{t('admin.orgMode.title')}</h1>
        <div className="admin-error" role="alert">
          <p>{error}</p>
          <button type="button" className="dashboard-error-retry" onClick={load}>
            {t('dashboard.retry')}
          </button>
        </div>
        <p className="admin-back">
          <Link to="/admin">{t('common.back')} → {t('admin.backToDashboard')}</Link>
        </p>
      </div>
    );
  }

  // 個人特定 → 匿名は破壊的操作（既存データが匿名化される）
  const isDestructive = mode?.consent_mode === 'personal_identified' && confirmTarget === 'anonymous';

  return (
    <div className="page admin-org-mode-page">
      <h1>{t('admin.orgMode.title')}</h1>
      <p className="admin-org-mode-intro">{t('admin.orgMode.intro')}</p>

      {mode && (
        <div className="admin-card admin-org-mode-card">
          <h3>{t('admin.orgMode.currentHeading')}</h3>
          <p className="admin-org-mode-tenant">
            {t('admin.orgMode.tenantLine', { name: mode.name, code: mode.tenant_code })}
          </p>

          <div
            className="admin-org-mode-options"
            role="radiogroup"
            aria-labelledby="admin-org-mode-heading"
          >
            <label
              className={`admin-org-mode-option${mode.consent_mode === 'anonymous' ? ' is-current' : ''}`}
            >
              <input
                type="radio"
                name="tenant-mode"
                value="anonymous"
                checked={mode.consent_mode === 'anonymous'}
                onChange={() => openConfirm('anonymous')}
                disabled={switching}
              />
              <span className="admin-org-mode-option-name">
                {t('admin.orgMode.anonymous')}
                {mode.consent_mode === 'anonymous' && (
                  <span className="admin-org-mode-current-badge"> ({t('admin.orgMode.currentBadge')})</span>
                )}
              </span>
              <span className="admin-org-mode-option-desc">{t('admin.orgMode.anonymousDesc')}</span>
            </label>
            <label
              className={`admin-org-mode-option${mode.consent_mode === 'personal_identified' ? ' is-current' : ''}`}
            >
              <input
                type="radio"
                name="tenant-mode"
                value="personal_identified"
                checked={mode.consent_mode === 'personal_identified'}
                onChange={() => openConfirm('personal_identified')}
                disabled={switching}
              />
              <span className="admin-org-mode-option-name">
                {t('admin.orgMode.personal')}
                {mode.consent_mode === 'personal_identified' && (
                  <span className="admin-org-mode-current-badge"> ({t('admin.orgMode.currentBadge')})</span>
                )}
              </span>
              <span className="admin-org-mode-option-desc">{t('admin.orgMode.personalDesc')}</span>
            </label>
          </div>

          {lastResult && (
            <div
              className={`admin-org-mode-result${lastResult.changed ? '' : ' is-noop'}`}
              role="status"
            >
              {lastResult.changed ? (
                <p>
                  {t('admin.orgMode.result.success', {
                    from: t(`admin.orgMode.${lastResult.previous_consent_mode}`),
                    to: t(`admin.orgMode.${lastResult.consent_mode}`),
                  })}
                </p>
              ) : (
                <p>{t('admin.orgMode.result.noop')}</p>
              )}
              {lastResult.revoked_consent_count > 0 && (
                <p>
                  {t('admin.orgMode.result.revoked', { count: lastResult.revoked_consent_count })}
                </p>
              )}
              {lastResult.anonymization_queued.length > 0 && (
                <p>
                  {t('admin.orgMode.result.anonymizationQueued', {
                    count: lastResult.anonymization_queued.length,
                  })}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {masterModes && masterModes.personal_identified.length > 0 && (
        <div className="admin-card">
          <h3>{t('admin.orgMode.personalDocsTitle')}</h3>
          <p className="admin-org-mode-docs-hint">{t('admin.orgMode.personalDocsHint')}</p>
          <ul className="admin-org-mode-docs-list">
            {masterModes.personal_identified.map((d) => (
              <li key={d.id}>
                {t('settings.consent.documentLine', {
                  title: d.title,
                  version: d.version,
                  date: new Date(d.effective_from).toLocaleDateString('ja-JP'),
                })}
              </li>
            ))}
          </ul>
        </div>
      )}

      {confirmTarget && mode && (
        <div className="admin-consent-docs-dialog-backdrop" role="dialog" aria-modal="true">
          <div className="admin-consent-docs-dialog">
            <h3>
              {t('admin.orgMode.confirm.title', {
                from: t(`admin.orgMode.${mode.consent_mode}`),
                to: t(`admin.orgMode.${confirmTarget}`),
              })}
            </h3>
            {isDestructive ? (
              <>
                <p className="admin-consent-docs-dialog-warning" role="alert">
                  {t('admin.orgMode.confirm.destructiveWarning')}
                </p>
                <ul className="admin-org-mode-confirm-list">
                  <li>{t('admin.orgMode.confirm.destructive1')}</li>
                  <li>{t('admin.orgMode.confirm.destructive2')}</li>
                  <li>{t('admin.orgMode.confirm.destructive3')}</li>
                </ul>
                <label className="admin-consent-docs-dialog-checkbox">
                  <input
                    type="checkbox"
                    checked={confirmAck}
                    onChange={(e) => setConfirmAck(e.target.checked)}
                  />
                  {t('admin.orgMode.confirm.ack')}
                </label>
              </>
            ) : (
              <p>{t('admin.orgMode.confirm.upgradeBody')}</p>
            )}

            {switchError && <p className="admin-form-error">{switchError}</p>}

            <div className="admin-consent-docs-dialog-actions">
              <button
                type="button"
                className="admin-btn-secondary"
                onClick={closeConfirm}
                disabled={switching}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className={isDestructive ? 'admin-btn-danger' : 'admin-btn-primary'}
                onClick={handleSwitch}
                disabled={switching || (isDestructive && !confirmAck)}
              >
                {switching ? t('common.loading') : t('admin.orgMode.confirm.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      <p className="admin-back">
        <Link to="/admin">{t('common.back')} → {t('admin.backToDashboard')}</Link>
      </p>
    </div>
  );
}
